import type { DailyContentKind } from "@/core/daily-content/kinds.js"

import { getCsrfTokenFromDocumentCookie } from "./auth-api"

export type DailyContentFallbackMode = "none" | "preset"

export type DailyContentProfile = {
  id: string
  name: string
  kind: DailyContentKind
  targetChannelId: string
  scheduleKind: "cron"
  scheduleValue: string
  fallbackMode: DailyContentFallbackMode
  pingRoleId: string | null
  pinMessage: boolean
  createThread: boolean
  isActive: boolean
  schedulerRefId: string | null
}

type DailyContentProfileInput = {
  name: string
  kind: DailyContentKind
  targetChannelId: string
  scheduleValue: string
  fallbackMode?: DailyContentFallbackMode
  pingRoleId?: string | null
  pinMessage?: boolean
  createThread?: boolean
}

type DailyContentProfilePatch = Partial<DailyContentProfileInput>

const readErrorMessage = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  try {
    const body = (await response.json()) as {
      error?: { message?: string }
      message?: string
    }

    return body.error?.message ?? body.message ?? fallback
  } catch {
    return fallback
  }
}

const getCsrfToken = () => {
  const csrfToken = getCsrfTokenFromDocumentCookie()
  if (!csrfToken) {
    throw new Error("Missing CSRF token")
  }

  return csrfToken
}

export const dailyContentApi = {
  listProfiles: async (): Promise<DailyContentProfile[]> => {
    const response = await fetch("/api/admin/daily-content/profiles", {
      method: "GET",
      credentials: "include",
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to load daily content profiles",
        ),
      )
    }

    const payload = (await response.json()) as { items: DailyContentProfile[] }
    return payload.items
  },
  createProfile: async (
    input: DailyContentProfileInput,
  ): Promise<DailyContentProfile> => {
    const response = await fetch("/api/admin/daily-content/profiles", {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-csrf-token": getCsrfToken(),
      },
      body: JSON.stringify(input),
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to create daily content profile",
        ),
      )
    }

    const payload = (await response.json()) as { item: DailyContentProfile }
    return payload.item
  },
  updateProfile: async (
    profileId: string,
    patch: DailyContentProfilePatch,
  ): Promise<DailyContentProfile> => {
    const response = await fetch(
      `/api/admin/daily-content/profiles/${profileId}`,
      {
        method: "PATCH",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          "x-csrf-token": getCsrfToken(),
        },
        body: JSON.stringify(patch),
      },
    )

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to update daily content profile",
        ),
      )
    }

    const payload = (await response.json()) as { item: DailyContentProfile }
    return payload.item
  },
  pauseProfile: async (profileId: string): Promise<DailyContentProfile> => {
    const response = await fetch(
      `/api/admin/daily-content/profiles/${profileId}/pause`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "x-csrf-token": getCsrfToken(),
        },
      },
    )

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to pause daily content profile",
        ),
      )
    }

    const payload = (await response.json()) as { item: DailyContentProfile }
    return payload.item
  },
  resumeProfile: async (profileId: string): Promise<DailyContentProfile> => {
    const response = await fetch(
      `/api/admin/daily-content/profiles/${profileId}/resume`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "x-csrf-token": getCsrfToken(),
        },
      },
    )

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to resume daily content profile",
        ),
      )
    }

    const payload = (await response.json()) as { item: DailyContentProfile }
    return payload.item
  },
  publishNow: async (profileId: string): Promise<void> => {
    const response = await fetch(
      `/api/admin/daily-content/profiles/${profileId}/publish-now`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "x-csrf-token": getCsrfToken(),
        },
      },
    )

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to publish daily content profile now",
        ),
      )
    }
  },
}
